import { createSelector } from 'reselect';
import createCachedSelector from 're-reselect';

import get from 'lodash/get';
import flatMap from 'lodash/flatMap';

import { ENTITIES_KEY_FORM_LOGIC_RULE, LOGIC_PROPERTY_REQUIRED, LOGIC_PROPERTY_VISIBLE } from './index';

import { getEntitySelector } from '../selectors';
import { createNewLogicRule, getNodeLogicRules } from './utils';

import { getRootNodeId, getAllForms } from '../form/selectors';
import { getFormNode, getFormNodes } from '../formNode/selectors';
import { getCrossGroupTargetNodes } from '../formNode/utils';

/**
 * @param {Object} state
 * @param {string} formId
 * @return {Array}
 */
const getRootNodeLogicRules = (state, formId) => {
    const rootNodeId = getRootNodeId(state, formId);
    const rootNode = getFormNode(state, rootNodeId);
    const logicRuleIds = get(rootNode, 'logic', []);
    const allLogicRules = getEntitySelector(state, ENTITIES_KEY_FORM_LOGIC_RULE);

    return Object.values(allLogicRules).filter((logicRule) => logicRuleIds.includes(logicRule.id)); // @TODO improve
};

/**
 * ⚠️ This returns all entities, including orphan ones.
 * @param {Object} state
 * @return {Object} - object with entities
 */
export const getLogicEntities = (state) => getEntitySelector(state, ENTITIES_KEY_FORM_LOGIC_RULE) || {};

/**
 * @param {Object} state
 * @return {Object[]} - array of logic rule entities
 */
export const getAllLogicRules = (state) => {
    const forms = getAllForms(state);
    return flatMap(forms, (form) => getRootNodeLogicRules(state, form.id));
};

export const getLogic = (state) => getEntitySelector(state, ENTITIES_KEY_FORM_LOGIC_RULE) || {};

/**
 * @param {string} propertyKey
 * @return {function}
 */
const getLogicRulesByProperty = (propertyKey) =>
    createCachedSelector(
        [getRootNodeLogicRules, (state, formId, propertyValue) => propertyValue],
        (formLogicRules, propertyValue) =>
            formLogicRules.filter((logicRule) => logicRule[propertyKey] === propertyValue),
    )((state, formId, propertyValue) => `${formId}:${propertyValue}`);

export const getFormLogicRules = getRootNodeLogicRules;

/**
 * @param {Object} state
 * @param {string} formId
 * @return {Array}
 */
export const getFormNodeLogicRules = getLogicRulesByProperty('target');

/**
 * @param {Object} state
 * @param {string} formId
 * @return {Array}
 */
export const getOptionLogicRules = getLogicRulesByProperty('defaultValue');

/**
 * @param {Object} state
 * @param {string} formId
 * @param {string} nodeId
 * @return {bool}
 */
export const getFormNodeRequiredLogicRule = createSelector([getFormNodeLogicRules], (formNodeLogicRules) =>
    formNodeLogicRules.find((logicRule) => logicRule.property === LOGIC_PROPERTY_REQUIRED),
);

/**
 * @param {Object} state
 * @param {string} logicRuleId
 * @return {Array}
 */
export const getFormLogicRule = (state, logicRuleId) =>
    getEntitySelector(state, ENTITIES_KEY_FORM_LOGIC_RULE)[logicRuleId];

/**
 * @param {Object} state
 * @param {string} formId
 * @param {string} nodeId
 * @return {bool}
 */
export const getFormNodeIsRequired = createCachedSelector(
    [getFormNodeRequiredLogicRule],
    (logicRule) => (logicRule && logicRule.defaultValue) || false,
)((state, formId, propertyValue) => propertyValue);

export const getFormNodeGroupIsRequired = (state, formNode) =>
    !formNode.children.map((childrenId) => getFormNodeIsRequired(state, formNode.formId, childrenId)).includes(false);

/**
 * @param {Object} state
 * @param {string} formId
 * @return {Array}
 */
export const getFormNodeVisibilityLogicRules = createSelector([getFormNodeLogicRules], (formNodeLogicRules) =>
    formNodeLogicRules.filter((logicRule) => logicRule.property === LOGIC_PROPERTY_VISIBLE),
);

/**
 * Returns:
 * The targetNode affected by the new added logic.
 * The form rootNode.
 * The given property (ex. visibility).
 * The new logicRule (In case of adding a new one, will have operands empty most times).
 *
 * @param {Object} state
 * @param {Object} data
 * @param {string} data.targetId
 * @param {string} data.property
 * @param {Object} data.logicRule
 * @returns {Object}
 */
export const getAddLogicData = (state, { targetId, property, logicRule }) => {
    const targetNode = getFormNode(state, targetId);
    const { formId } = targetNode;
    const rootNodeId = getRootNodeId(state, formId);
    const rootNode = getFormNode(state, rootNodeId);

    return {
        targetNode,
        rootNode,
        property,
        logicRule,
    };
};

/**
 * Returns:
 * An array of all the node logic rules ids to remove.
 * All the form nodes.
 * The affected node by the logic removal.
 *
 * @param {Object} state
 * @param {Object} data
 * @param {[]string} data.targetId
 * @returns {Object}
 */
export const getRemoveLogicData = (state, { logicRuleIds }) => {
    const logicRule = getFormLogicRule(state, logicRuleIds[0]);
    const { target, formId } = logicRule;
    const formNodes = getFormNodes(state, formId);
    const targetNode = getFormNode(state, target);

    return {
        logicRuleIds,
        formNodes,
        targetNode,
    };
};

/**
 * Returns:
 * The complete updated logicRule.
 * The updates object to apply.
 * The affected node by the logic updates.
 *
 * @param {Object} state
 * @param {Object} data
 * @param {string} data.targetId
 * @param {Object} data.updates
 * @returns {Object}
 */
export const getUpdateLogicData = (state, { logicRuleId, updates }) => {
    const logicRule = getFormLogicRule(state, logicRuleId);
    const { target } = logicRule;
    const targetNode = getFormNode(state, target);

    return {
        logicRule,
        updates,
        targetNode,
    };
};

/**
 * Returns the data to be used by the payload for multiple logic additions.
 *
 * @param {Object} state
 * @param {Object} data
 * @param {[]Object} data.multipleAddEffects
 * @returns {Object}
 */
export const getAddMultipleLogicData = (state, { multipleAddEffects }) => {
    const formId = get(multipleAddEffects[0], 'meta.logicRule.formId');
    const rootNodeId = getRootNodeId(state, formId);
    const rootNode = getFormNode(state, rootNodeId);

    const logicRules = multipleAddEffects.map(({ meta }) => {
        const { targetId, property, logicRule } = meta;
        const targetNode = getFormNode(state, targetId);
        return createNewLogicRule(targetNode, logicRule, property);
    });

    return {
        rootNode,
        logicRules,
    };
};

/**
 * Returns:
 * An object containing all the formLogicRules.
 * The given node assigned logicRules as nodeLogicRules.
 * Every plus one guest formNode cloned from the given one in crossGroupTargetNodes.
 *
 * @param {Object} state
 * @param {string} formId
 * @param {Object} targetNode
 * @param {[]Object} formNodes
 * @returns {Object}
 */
export const getAdditionalGuestNodesData = (state, formId, targetNode, formNodes) => {
    const formLogicRules = getFormLogicRules(state, formId);
    const nodeLogicRules = getNodeLogicRules(formLogicRules, targetNode.id);
    const crossGroupTargetNodes = getCrossGroupTargetNodes(formNodes, targetNode);

    return {
        formLogicRules,
        nodeLogicRules,
        crossGroupTargetNodes,
    };
};
